import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "shared/contexts/AuthProvider";
import { organizationsService } from "features/administration/services";

const SELECTED_ORG_STORAGE_KEY = "selectedOrgId";

const UserOrgContext = createContext(null);

/**
 * UserOrgProvider
 *
 * Manages:
 *  - The list of organizations the current user belongs to, with their roles
 *  - The currently selected working organization (persisted in localStorage)
 *
 * Exposes:
 *  - userOrgs          — [{organization, roles}] for all orgs the user belongs to
 *  - selectedOrg       — the active working org entry ({organization, roles}) or null
 *  - setSelectedOrg    — switch the active working org (persists to localStorage)
 *  - rolesInSelectedOrg — string[] of roles in the selected org
 *  - hasRoleInSelectedOrg(role) — boolean helper
 *  - loading           — true while fetching
 *  - ready             — true once the org list has been fetched for the CURRENT
 *                        auth state. Unlike `loading`, it is already false in the
 *                        render right after login/auth-restore, before the fetch
 *                        effect runs — so role checks never run against an empty list.
 *  - error             — error message string or null
 *  - refreshUserOrgs   — manually re-fetch orgs from API
 */
export function UserOrgProvider({ children }) {
  const { isAuthenticated, authFetch } = useAuth();

  const [userOrgs, setUserOrgs] = useState([]);
  const [selectedOrg, setSelectedOrgState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // `isAuthenticated` value the org list was last fetched for (null = never).
  const [fetchedForAuth, setFetchedForAuth] = useState(null);

  const fetchUserOrgs = useCallback(async () => {
    if (!isAuthenticated) {
      setUserOrgs([]);
      setSelectedOrgState(null);
      setFetchedForAuth(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all user orgs in one call (users typically belong to few orgs)
      const response = await organizationsService.getMyOrganizationsRoles(authFetch, 1, 0);      
      const orgs = response.data?.results ?? [];

      setUserOrgs(orgs);

      // Restore previously selected org from localStorage, fall back to first
      const storedOrgId = localStorage.getItem(SELECTED_ORG_STORAGE_KEY);
      if (storedOrgId) {
        const match = orgs.find(
          (entry) => entry.organization?.org_id === storedOrgId
        );
        setSelectedOrgState(match ?? orgs[0] ?? null);
      } else {
        setSelectedOrgState(orgs[0] ?? null);
      }
    } catch (err) {
      setError(err.message ?? "Failed to load organizations");
    } finally {
      setLoading(false);
      setFetchedForAuth(true);
    }
  }, [isAuthenticated, authFetch]);

  // Re-fetch whenever auth state changes (login / logout)
  useEffect(() => {
    fetchUserOrgs();
  }, [fetchUserOrgs]);

  /**
   * Switch the active working organization.
   * Accepts an org entry object ({organization, roles}) or null to deselect.
   */
  const setSelectedOrg = useCallback((orgEntry) => {
    setSelectedOrgState(orgEntry);
    if (orgEntry?.organization?.org_id) {
      localStorage.setItem(SELECTED_ORG_STORAGE_KEY, orgEntry.organization.org_id);
    } else {
      localStorage.removeItem(SELECTED_ORG_STORAGE_KEY);
    }
  }, []);

  const ready = fetchedForAuth === isAuthenticated;

  const rolesInSelectedOrg = selectedOrg?.roles ?? [];

  const hasRoleInSelectedOrg = useCallback(
    (role) => rolesInSelectedOrg.includes(role),
    [rolesInSelectedOrg]
  );

  const rolesInOrg = useCallback(
    (org_id) => {
      const orgEntry = userOrgs.find((entry) => entry.organization?.org_id === org_id);
      return orgEntry?.roles ?? [];
    },
    [userOrgs]
  );

  const hasRoleInOrg = useCallback(
    (org_id, role) => {
      return rolesInOrg(org_id)?.includes(role) ?? false;
    },
    [rolesInOrg]
  );  

  return (
    <UserOrgContext.Provider
      value={{
        userOrgs,
        selectedOrg,
        setSelectedOrg,
        rolesInSelectedOrg,
        hasRoleInSelectedOrg,
        rolesInOrg,
        hasRoleInOrg,
        loading,
        ready,
        error,
        refreshUserOrgs: fetchUserOrgs,
      }}
    >
      {children}
    </UserOrgContext.Provider>
  );
}

export const useUserOrg = () => useContext(UserOrgContext);
